import React from "react";
import { Phone, ChatText } from "@phosphor-icons/react";

export const DEFAULT_TY = {
  display: { show_logo: true, show_firm_name: true, show_call_text: true, show_timeline: true, show_buyer_message: true },
  timeline: [
    { title: "Case received", body: "Your answers were sent securely for review." },
    { title: "Attorney review", body: "An intake specialist looks over what happened." },
    { title: "We reach out", body: "Expect a call or text at the number you gave us — keep your phone nearby." },
  ],
};

const digits = (p) => (p || "").replace(/[^0-9+]/g, "");

export const FirmBlock = ({ logoUrl, firm, display, dark }) => {
  const showLogo = display.show_logo && logoUrl;
  const showName = display.show_firm_name && firm;
  if (!showLogo && !showName) return null;
  return (
    <div className="mb-5 flex flex-col items-center gap-2" data-testid="thank-you-firm-block">
      {showLogo && (
        <img src={logoUrl} alt={firm || "Law firm logo"} className="h-14 w-auto max-w-[200px] rounded-md bg-white object-contain p-1.5" data-testid="thank-you-firm-logo" />
      )}
      {showName && (
        <div className={`text-sm font-bold uppercase tracking-[0.16em] ${dark ? "text-slate-200" : "text-[#0a1929]"}`} data-testid="thank-you-firm-name">{firm}</div>
      )}
    </div>
  );
};

export const ContactButtons = ({ phone, label }) => {
  const base = "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full px-6 text-base font-bold transition-transform duration-200 hover:-translate-y-0.5";
  return (
    <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row" data-testid="thank-you-contact-buttons">
      <a href={`tel:${digits(phone)}`} className={`${base} cf-cta bg-[var(--cf-accent)] text-white`} data-testid="thank-you-tap-to-call-button">
        <Phone size={18} weight="fill" /> Call {label}
      </a>
      <a href={`sms:${digits(phone)}`} className={`${base} border border-white/20 bg-white/[0.06] text-white`} data-testid="thank-you-tap-to-text-button">
        <ChatText size={18} weight="fill" /> Text {label}
      </a>
    </div>
  );
};

export const BuyerMessage = ({ text, firm, dark }) => (
  <div className={`mx-auto mt-8 max-w-xl rounded-2xl p-5 text-left ${dark ? "border border-white/10 bg-white/[0.04]" : "border-2 border-[#0a1929] bg-white"}`} data-testid="thank-you-buyer-message">
    <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${dark ? "text-slate-300" : "text-slate-500"}`}>
      A note from {firm || "your legal team"}
    </div>
    <p className={`mt-2 whitespace-pre-line text-[15px] leading-relaxed ${dark ? "text-slate-200" : "text-slate-700"}`}>{text}</p>
  </div>
);

export const NextSteps = ({ steps, dark }) => (
  <div className="mx-auto mt-10 max-w-xl text-left" data-testid="thank-you-timeline-container">
    <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${dark ? "text-slate-400" : "text-slate-500"}`}>What happens next</div>
    <ol className="mt-4">
      {steps.map((s, i) => (
        <li key={i} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`thank-you-timeline-step-${i}`}>
          {i < steps.length - 1 && <span className={`absolute bottom-0 left-[15px] top-8 w-px ${dark ? "bg-white/15" : "bg-slate-300"}`} aria-hidden="true" />}
          <span className={`relative grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold text-white ${dark ? "bg-[var(--cf-accent)]" : "bg-[#0a1929]"}`}>{i + 1}</span>
          <div>
            <div className={`font-bold ${dark ? "text-white" : "text-[#0a1929]"}`}>{s.title}</div>
            {s.body && <p className={`mt-0.5 text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{s.body}</p>}
          </div>
        </li>
      ))}
    </ol>
  </div>
);
